import { useEffect, useRef, useSyncExternalStore } from 'react'

/**
 * Звуки партии.
 *
 * Зачем вообще. Про чей ход экран говорит подсвеченным углом доски и строкой
 * снизу, но и то и другое надо ВИДЕТЬ. Ход приходит, когда смотришь в сторону
 * или свернул игру, а за одним устройством — когда телефон только что дали
 * в руки. Дальше тот же довод распространяется на события, которые случаются
 * не по вашей воле: срубили вашу фишку, кто-то дошёл до дома, партия кончилась.
 *
 * Чего здесь НЕТ намеренно — тика на каждой клетке пути. Шаг показа 110 мс
 * (STEP_MS в usePlayback), то есть девять щелчков в секунду: это не поступь
 * фишки, а стрёкот. Ровно та же причина, по которой в ui.css нет подскока
 * на каждой клетке.
 *
 * Звучит синтезом, но играет файл, если он положен: см. warmSounds() и
 * frontend/public/sounds/README.md. Синтез — не «пока нет файлов», а рабочий
 * запас: игра со свежего клона обязана звучать, ничего не докачивая.
 */
export type Cue =
  | 'start'
  | 'turn'
  | 'roll'
  | 'bonus'
  | 'exit'
  | 'capture'
  | 'home'
  | 'win'
  | 'lose'
  | 'chat'

/** Нота. `to` — куда съезжает высота: так звучит удар, а не звонок. */
type Tone = { at: number; hz: number; to?: number; len: number; loud?: number }

/** Стук: полоса шума. У дерева нет высоты, и нотой его не изобразить. */
type Knock = { at: number; hz: number; len: number; loud?: number }

/**
 * Голоса.
 *
 * Числа настраиваются на слух, и это единственное место, где их надо
 * править. Каждому голосу — свой довод, иначе набор быстро превращается
 * в игровой автомат.
 */
const BANK: Record<Cue, { tones?: Tone[]; knocks?: Knock[] }> = {
  /*
   * Партия началась: то же движение вверх, что у победы, но короче и с
   * квинты, а не с октавы. Пара «начало — конец» должна слышаться родственной
   * и при этом различаться: начало обещает, конец подводит итог.
   */
  start: {
    tones: [
      { at: 0, hz: 523.25, len: 0.12 },
      { at: 0.09, hz: 659.25, len: 0.12 },
      { at: 0.18, hz: 783.99, len: 0.28 },
    ],
  },
  /*
   * Ход перешёл: ми и си пятой октавы — чистая квинта вверх. Восходящий
   * интервал слышится незаконченным, то есть как приглашение сходить;
   * нисходящий на этом месте читался бы как «всё, конец».
   */
  turn: {
    tones: [
      { at: 0, hz: 659.25, len: 0.13 },
      { at: 0.085, hz: 987.77, len: 0.22 },
    ],
  },
  /*
   * Кубики: три деревянных стука вразнобой, каждый тише предыдущего. Ноты
   * здесь нет вовсе — у падающего кубика нет высоты, есть шум в полосе
   * около килогерца.
   */
  roll: {
    knocks: [
      { at: 0, hz: 1100, len: 0.05 },
      { at: 0.055, hz: 900, len: 0.045, loud: 0.75 },
      { at: 0.12, hz: 1300, len: 0.06, loud: 0.5 },
    ],
  },
  /*
   * Доброс: те же стуки, что и у обычного броска, но перед ними короткий
   * подскок вверх. Доброс — это НАГРАДА, и узнаваться он должен до того, как
   * игрок посмотрит на кубики; при этом он всё ещё бросок, и с нуля другой
   * голос завёл бы в игре второй язык.
   */
  bonus: {
    tones: [{ at: 0, hz: 880, len: 0.07, loud: 0.7 }],
    knocks: [
      { at: 0.05, hz: 1100, len: 0.05 },
      { at: 0.105, hz: 900, len: 0.045, loud: 0.75 },
      { at: 0.17, hz: 1300, len: 0.06, loud: 0.5 },
    ],
  },
  /*
   * Фишка вышла из двора: один стук пониже броска и короткий тон вверх —
   * «встала на доску». Событие частое (по пять на игрока за партию), поэтому
   * коротко и без праздника.
   */
  exit: {
    knocks: [{ at: 0, hz: 700, len: 0.05, loud: 0.9 }],
    tones: [{ at: 0.03, hz: 392, to: 587.33, len: 0.12, loud: 0.7 }],
  },
  /*
   * Рубка: удар. Стук плюс тон, съезжающий с 200 до 70 герц, — так звучит
   * тяжёлое, поставленное на стол. Громче остальных намеренно: это
   * единственное событие в игре, которое отбирает чужое.
   */
  capture: {
    knocks: [{ at: 0, hz: 520, len: 0.07, loud: 1.3 }],
    tones: [{ at: 0, hz: 200, to: 70, len: 0.16, loud: 1.3 }],
  },
  /*
   * Фишка дошла до дома: трезвучие вверх, коротко. За партию это случится
   * до двадцати раз, и растянутая фанфара надоест на пятый.
   */
  home: {
    tones: [
      { at: 0, hz: 880, len: 0.09, loud: 0.8 },
      { at: 0.06, hz: 1108.73, len: 0.09, loud: 0.8 },
      { at: 0.12, hz: 1318.51, len: 0.2, loud: 0.8 },
    ],
  },
  /*
   * За столом что-то сказали: две тихие ноты вверх на терцию, очень коротко.
   *
   * Тише и мельче всех остальных намеренно. Реплика — не событие партии:
   * по ней ничего не надо делать, и знак к ней обязан быть слабее любого
   * игрового, иначе разговор перебивает саму игру. Своей реплики не слышно
   * вовсе (см. useChat), а заткнутого соперника — тем более.
   */
  chat: {
    tones: [
      { at: 0, hz: 1046.5, len: 0.05, loud: 0.35 },
      { at: 0.045, hz: 1318.51, len: 0.09, loud: 0.35 },
    ],
  },
  /*
   * Победа: то же движение вверх, но с октавой сверху и вдвое длиннее.
   * Играет один раз за партию — здесь можно не экономить.
   */
  win: {
    tones: [
      { at: 0, hz: 659.25, len: 0.16 },
      { at: 0.12, hz: 830.61, len: 0.16 },
      { at: 0.24, hz: 987.77, len: 0.16 },
      { at: 0.36, hz: 1318.51, len: 0.5 },
    ],
  },
  /*
   * Поражение: зеркало победы — то же семейство нот, но вниз, и последняя
   * съезжает ещё на октаву: партия «сдулась», а не кончилась. Свой голос,
   * а не общая фанфара: иначе проигравшему играет чужой праздник, а слова
   * на экране при этом — «Победил Миша».
   *
   * Играет, как и победа, один раз за партию — длина здесь позволительна.
   */
  lose: {
    tones: [
      { at: 0, hz: 987.77, len: 0.16 },
      { at: 0.12, hz: 830.61, len: 0.16 },
      { at: 0.24, hz: 659.25, len: 0.16 },
      { at: 0.36, hz: 440, to: 220, len: 0.45 },
    ],
  },
}

/*
 * Потолок громкости — то, что получает голос на полном ползунке.
 *
 * Было 0,1 из соображения «это подсказки, а не события», и оказалось просто
 * тихо: на телефоне в комнате звука было не слышно вовсе. Полшкалы — предел,
 * за которым начинает подрезать: у рубки свой множитель 1,3, и на потолке
 * она даёт 0,65 — ещё с запасом до единицы.
 */
const PEAK = 0.5

/**
 * Громкость с ползунка, множителем к PEAK.
 *
 * Отдельной функцией, а не полем: голос собирается из нескольких нот, и
 * каждая спрашивает громкость в момент, когда её ставят в расписание. Так
 * ползунок слышно сразу, без пересборки чего-либо.
 */
function level(): number {
  return volume / 100
}

type Legacy = { webkitAudioContext?: typeof AudioContext }

let box: AudioContext | null = null

/**
 * Контекст один на всю партию.
 *
 * Создаётся при первом звуке или при входе в партию (warmSounds), но не при
 * загрузке страницы: на лобби, правилах и профиле он висел бы без дела, а
 * до касания страницы браузер всё равно держит его приостановленным.
 */
function context(): AudioContext | null {
  if (box !== null) return box

  const Make = window.AudioContext ?? (window as typeof window & Legacy).webkitAudioContext

  if (Make === undefined) return null

  try {
    box = new Make()
  } catch {
    // Звук — не обязательная часть игры: без него работает всё остальное.
    return null
  }

  return box
}

let noise: AudioBuffer | null = null

/** Шум для стуков: 0,4 с хватает на любой, дальше буфер переиспользуется. */
function noiseOf(audio: AudioContext): AudioBuffer {
  if (noise !== null) return noise

  const frames = Math.floor(audio.sampleRate * 0.4)
  const made = audio.createBuffer(1, frames, audio.sampleRate)
  const data = made.getChannelData(0)

  for (let i = 0; i < frames; i += 1) data[i] = Math.random() * 2 - 1

  noise = made

  return made
}

/**
 * Огибающая: быстрый подъём и затухание.
 *
 * Обязательна. Звук, оборванный на полной громкости, даёт щёлчок: волна
 * прерывается посреди периода. По той же причине она идёт не в нуль —
 * exponentialRampToValueAtTime нулевого значения не принимает вовсе.
 */
function envelope(gain: AudioParam, loud: number, at: number, len: number): void {
  gain.setValueAtTime(0.0001, at)
  gain.exponentialRampToValueAtTime(loud, at + Math.min(0.012, len * 0.3))
  gain.exponentialRampToValueAtTime(0.0001, at + len)
}

function note(audio: AudioContext, tone: Tone, start: number): void {
  const wave = audio.createOscillator()
  const gain = audio.createGain()
  const at = start + tone.at

  // Треугольная волна: синус на такой короткой ноте звучит глухо, а пила
  // и прямоугольник — по-будильниковому резко.
  wave.type = 'triangle'
  wave.frequency.setValueAtTime(tone.hz, at)

  if (tone.to !== undefined) {
    wave.frequency.exponentialRampToValueAtTime(tone.to, at + tone.len * 0.6)
  }

  envelope(gain.gain, PEAK * level() * (tone.loud ?? 1), at, tone.len)
  wave.connect(gain).connect(audio.destination)
  wave.start(at)
  wave.stop(at + tone.len + 0.02)
}

function tap(audio: AudioContext, knock: Knock, start: number): void {
  const source = audio.createBufferSource()
  const band = audio.createBiquadFilter()
  const gain = audio.createGain()
  const at = start + knock.at

  source.buffer = noiseOf(audio)
  // Полосовой фильтр и делает из белого шума стук: без него это «пшш».
  band.type = 'bandpass'
  band.frequency.value = knock.hz
  band.Q.value = 1.4

  envelope(gain.gain, PEAK * level() * (knock.loud ?? 1), at, knock.len)
  source.connect(band).connect(gain).connect(audio.destination)
  source.start(at)
  source.stop(at + knock.len + 0.02)
}

/** Готовый звук из public/sounds — если он там лежит. */
type Ready = { buffer: AudioBuffer; gain: number }

const files = new Map<Cue, Ready>()

/** Подать знак. С выключенным звуком молчит. */
export function play(cue: Cue): void {
  if (volume === 0) return

  const audio = context()

  if (audio === null) return

  /*
   * Пока страницу не коснулись, браузер держит контекст приостановленным и
   * звука не даёт — это политика автовоспроизведения, обойти её нельзя и не
   * нужно. Первый знак может пройти молча, дальше уже нет: и бросок, и ход
   * сами по себе касания.
   */
  if (audio.state === 'suspended') void audio.resume().catch(() => {})

  const start = audio.currentTime + 0.02
  const ready = files.get(cue)

  if (ready !== undefined) {
    const source = audio.createBufferSource()
    const gain = audio.createGain()

    source.buffer = ready.buffer
    gain.gain.value = ready.gain * level()
    source.connect(gain).connect(audio.destination)
    source.start(start)

    return
  }

  const voice = BANK[cue]

  for (const tone of voice.tones ?? []) note(audio, tone, start)
  for (const knock of voice.knocks ?? []) tap(audio, knock, start)
}

let warmed = false

/**
 * Подхватить готовые звуки, если они положены в public/sounds.
 *
 * Через список `sounds.json`, а не перебором имён: перебор дал бы по 404 на
 * каждый ненайденный звук в консоли и в сети при каждом входе в партию, а
 * список ещё и место для лицензии — у бесплатных библиотек она бывает
 * с обязательным упоминанием автора. Формат и порядок — в README рядом.
 *
 * Звука, которого в списке нет, это не касается: он играет синтезом.
 *
 * Зовётся на входе в партию, а не при первом звуке: иначе первый знак
 * прозвучал бы синтезом, а следующий — файлом, и это слышно.
 */
export function warmSounds(): void {
  if (warmed) return

  warmed = true

  const audio = context()

  if (audio === null) return

  void (async () => {
    try {
      const answer = await fetch('/sounds/sounds.json')

      if (!answer.ok) return

      const list = (await answer.json()) as Record<string, { file?: string; gain?: number }>

      await Promise.all(
        Object.entries(list).map(async ([cue, recipe]) => {
          // Неизвестное имя молча пропускается: список правит человек, и
          // опечатка в нём не должна ронять остальные звуки.
          if (!(cue in BANK) || typeof recipe?.file !== 'string') return

          const sound = await fetch(`/sounds/${recipe.file}`)

          if (!sound.ok) return

          files.set(cue as Cue, {
            buffer: await audio.decodeAudioData(await sound.arrayBuffer()),
            // Запись уже нормализована и рядом с синтезом звучит вдвое
            // громче, поэтому у файла своя громкость — её и крутят на слух.
            gain: typeof recipe.gain === 'number' ? recipe.gain : 0.5,
          })
        }),
      )
    } catch {
      // Файлов нет, список битый или сеть отказала — играет синтез.
    }
  })()
}

/*
 * Громкость. Хранится там же, где тема и вид поля, и по той же причине: это
 * настройка не партии, а того, как игра себя ведёт у конкретного игрока.
 *
 * Одним числом 0…100, и НОЛЬ — это «выключено». Отдельного выключателя нет
 * намеренно: два состояния на одну вещь пришлось бы сводить друг с другом, и
 * человек, приглушивший звук до десяти, а потом нажавший «включить», не знал
 * бы, куда попадёт. У ползунка такого вопроса нет — где стоит, там и стоит.
 *
 * Кнопки звука в шапке тоже больше нет. Держали её там потому, что гасят звук
 * посреди партии, а не заранее, и уходить за этим на другой экран значило
 * уйти с хода. Настройки открываются модальным окном поверх доски, так что
 * уходить больше не надо.
 *
 * По умолчанию ВКЛЮЧЕНО, и громко. Звук здесь не украшение, а канал
 * сообщения: выключенным по умолчанию его никто не нашёл бы, а тихим —
 * не услышал.
 */
const KEY = 'ludo.volume'
/** Прежний ключ — только чтобы не переспрашивать того, кто звук уже выключил. */
const OFF_KEY = 'ludo.sound'

const DEFAULT = 70

function clamp(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)))
}

function stored(): number {
  try {
    const saved = localStorage.getItem(KEY)

    if (saved !== null) {
      const value = Number(saved)

      return Number.isFinite(value) ? clamp(value) : DEFAULT
    }

    // Ползунка ещё не было, а выключатель был: уважаем прежний выбор.
    return localStorage.getItem(OFF_KEY) === 'off' ? 0 : DEFAULT
  } catch {
    // Приватный режим: выбор просто не запомнится.
    return DEFAULT
  }
}

let volume: number = stored()
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export function setVolume(next: number): void {
  const value = clamp(next)

  if (value === volume) return

  volume = value

  try {
    localStorage.setItem(KEY, String(value))
  } catch {
    // см. stored()
  }

  for (const listener of listeners) listener()
}

/*
 * Соседние вкладки слушают тоже.
 *
 * Событие storage приходит во все вкладки, КРОМЕ той, где меняли. Без него
 * настройка живёт копией на вкладку: убавил звук в настройках, а партия
 * в соседней вкладке продолжает звенеть по-прежнему — и выглядит это ровно
 * как «не работает». Пустой ключ означает, что данные сайта стёрли целиком, —
 * тогда перечитываем и это.
 */
window.addEventListener('storage', (event) => {
  if (event.key !== null && event.key !== KEY && event.key !== OFF_KEY) return

  const next = stored()

  if (next === volume) return

  volume = next

  for (const listener of listeners) listener()
})

export function useVolume(): number {
  // Третий аргумент — значение для сервера; рендера на сервере в проекте нет,
  // но без него useSyncExternalStore падает в тестовой среде.
  return useSyncExternalStore(
    subscribe,
    () => volume,
    () => volume,
  )
}

/**
 * Знак на смену метки: цвета ходящего, номера броска, состояния партии.
 *
 * Метка — то, что ПОКАЗЫВАЕТСЯ, а не то, что записано в партии: звук обязан
 * совпадать со строкой и подсветкой, а они отстают от сервера, пока
 * доигрывает предыдущий ход.
 *
 * `ring` решает, звонить ли вообще. Пример: ход перешёл — в сетевой партии
 * звонок только за свой ход, потому что на чужом делать по нему нечего;
 * за одним устройством за каждый, там смысл ровно в том, что телефон пора
 * передать.
 *
 * Звонок — только на ПЕРЕХОДЕ между двумя значениями. Отсюда две проверки:
 *
 * previous === null отсекает открытие страницы. Пока партия загружается,
 * ходящего нет, и без этого игра здоровалась бы звуком на каждой
 * перезагрузке — в том числе на чужом ходу и в доигранной партии.
 *
 * previous === mark отсекает повтор. Шестёрка даёт добросить, и ход при этом
 * никуда не уходит: передачи не было, звонить незачем. Заодно это делает
 * эффект идемпотентным, а он обязан быть таким — StrictMode прогоняет
 * эффекты дважды.
 */
export function useCueOnChange(cue: Cue, mark: string | number | null, ring = true): void {
  const before = useRef<string | number | null>(null)

  useEffect(() => {
    const previous = before.current

    before.current = mark

    if (previous === null || previous === mark || mark === null) return
    if (ring) play(cue)
  }, [cue, mark, ring])
}
